import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, Copy, Check, CalendarCheck, Clock, Puzzle, Mail, PartyPopper, X } from 'lucide-react'
import { MarketingNav } from '../components/MarketingNav'
import { MarketingFooter } from '../components/MarketingFooter'
import { useWindowWidth } from '../hooks/useWindowWidth'

// Landingpage hinter dem QR-Code bzw. NFC-Chip der Visitenkarte.
// Herkunft kommt als ?src=… (z. B. visitenkarte, salon, praxis, messe-okt)
// und wird ohne Cookies nur für diese Sitzung gemerkt.
// Die Live-Demo ist das echte chat-widget.js in /widget-demo.html, dessen
// Server-Antworten lokal simuliert werden (public/widget-demo-backend.js).

const PROMO_CODE = 'KARTE'
const SRC_STORAGE_KEY = 'candlear_src'

type DemoNotice =
  | { kind: 'code'; code: string; email: string }
  | { kind: 'booked'; when: string; email: string }

function readSource(): string {
  const fromUrl = new URLSearchParams(window.location.search).get('src')
  const clean = fromUrl?.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40)
  try {
    if (clean) sessionStorage.setItem(SRC_STORAGE_KEY, clean)
    return clean || sessionStorage.getItem(SRC_STORAGE_KEY) || 'direkt'
  } catch {
    return clean || 'direkt'
  }
}

// Die Demo meldet „E-Mail mit Code“ und „Termin gebucht“ an die Seite,
// weil in der Demo keine echte E-Mail verschickt wird.
function useDemoNotices() {
  const [notice, setNotice] = useState<DemoNotice | null>(null)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || !e.data || typeof e.data !== 'object') return
      const d = e.data as Record<string, string>
      let next: DemoNotice | null = null
      if (d.type === 'candlear-demo-code') next = { kind: 'code', code: d.code, email: d.email }
      if (d.type === 'candlear-demo-booked') next = { kind: 'booked', when: d.when, email: d.email }
      if (!next) return
      setNotice(next)
      window.clearTimeout(timer.current)
      if (next.kind === 'booked') timer.current = window.setTimeout(() => setNotice(null), 9000)
    }
    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('message', onMessage)
      window.clearTimeout(timer.current)
    }
  }, [])

  return [notice, () => setNotice(null)] as const
}

const BENEFITS = [
  { icon: Clock, title: 'Rund um die Uhr', text: 'Kundinnen und Kunden buchen auch abends, ohne dass das Telefon klingelt.', color: '#FF9A00' },
  { icon: CalendarCheck, title: 'Keine Doppelbuchungen', text: 'Freie Zeiten kommen direkt aus deinem Kalender, mit Pausen und Puffern.', color: '#FF6D00' },
  { icon: Puzzle, title: 'In Minuten eingebunden', text: 'Ein Code-Schnipsel für jede Website, oder das offizielle WordPress-Plugin.', color: '#F6EEE4' },
]

const eyebrow: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 700,
  letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FF9A00', margin: '0 0 12px',
}

export default function KartePage() {
  const isMobile = useWindowWidth() < 900
  const reduceMotion = useReducedMotion()
  const [source] = useState(readSource)
  const [copied, setCopied] = useState(false)
  const [notice, closeNotice] = useDemoNotices()

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* Code steht ohnehin sichtbar da */
    }
  }

  const rise = (delay: number) => ({
    initial: { opacity: 0, y: reduceMotion ? 0 : 16 },
    animate: { opacity: 1, y: 0 },
    transition: { type: 'spring' as const, stiffness: 160, damping: 22, delay },
  })

  const intro = (
    <>
      <motion.p {...rise(0)} style={eyebrow}>Schön, dass du die Karte gescannt hast</motion.p>
      <motion.h1 {...rise(0.06)} style={{
        fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800,
        fontSize: isMobile ? 'clamp(30px, 8.4vw, 40px)' : 'clamp(40px, 4.6vw, 56px)',
        lineHeight: 1.06, letterSpacing: '-0.03em', color: '#F6EEE4', margin: 0,
      }}>
        Probier Candlear Booking <span style={{ color: '#FF6D00' }}>live aus.</span>
      </motion.h1>
      <motion.p {...rise(0.12)} style={{
        fontFamily: 'Inter, sans-serif', fontSize: isMobile ? 16 : 18, lineHeight: 1.6,
        color: 'rgba(246,238,228,0.68)', margin: '16px 0 0', maxWidth: 520,
      }}>
        {isMobile
          ? 'Unten läuft das echte Buchungswidget eines Beispielsalons. Schreib dem Assistenten oder buch über „Termin buchen“.'
          : 'Rechts läuft das echte Buchungswidget eines Beispielsalons. Schreib dem Assistenten oder buch über „Termin buchen“, genau so erleben es deine Kundinnen und Kunden.'}
      </motion.p>
    </>
  )

  const offer = (
    <motion.section {...rise(0.22)} style={{
      display: 'flex', flexDirection: 'column', gap: 18,
      padding: isMobile ? 22 : 28, borderRadius: 20,
      background: 'linear-gradient(150deg, rgba(36,26,69,0.9) 0%, rgba(21,17,44,0.9) 55%, rgba(120,45,10,0.55) 100%)',
      border: '1px solid rgba(255,109,0,0.28)',
      boxShadow: '0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 60px -28px rgba(255,109,0,0.45)',
    }}>
      <div>
        <p style={{ ...eyebrow, margin: '0 0 8px' }}>Dein Kartenvorteil</p>
        <h2 style={{
          fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: isMobile ? 21 : 24,
          lineHeight: 1.25, color: '#F6EEE4', margin: 0, letterSpacing: '-0.01em',
        }}>
          30 Tage kostenlos testen, danach 1 Monat gratis.
        </h2>
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, lineHeight: 1.6, color: 'rgba(246,238,228,0.6)', margin: '8px 0 0' }}>
          Code beim Abschluss des Abos im Bezahlschritt eingeben.
        </p>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <button type="button" onClick={copyCode} className="karte-code" aria-label={`Code ${PROMO_CODE} kopieren`}>
          <span>{PROMO_CODE}</span>
          {copied ? <Check size={18} /> : <Copy size={18} />}
        </button>
        <a href="/admin" className="karte-btn karte-btn-primary" style={{ flex: isMobile ? '1 1 100%' : '0 0 auto', justifyContent: 'center' }}>
          Kostenlos testen <ArrowRight size={18} />
        </a>
      </div>
    </motion.section>
  )

  const demo = (
    <motion.div {...rise(0.16)} style={{ width: '100%', maxWidth: 410, margin: '0 auto' }}>
      <div className="karte-device">
        <iframe
          src="/widget-demo.html"
          title="Live-Demo: Candlear Buchungswidget"
          loading="eager"
          style={{
            display: 'block', width: '100%', border: 0, borderRadius: 22, background: '#fff',
            height: isMobile ? 'min(640px, calc(100svh - 96px))' : 680,
          }}
        />
      </div>
      <p style={{
        fontFamily: 'Inter, sans-serif', fontSize: 12.5, lineHeight: 1.5, textAlign: 'center',
        color: 'rgba(246,238,228,0.45)', margin: '14px 8px 0',
      }}>
        Live-Demo mit erfundenem Salon. Es wird nichts gespeichert und keine echte E-Mail verschickt.
      </p>
    </motion.div>
  )

  return (
    <div style={{ position: 'relative', background: '#0E0B1E', minHeight: '100vh', overflowX: 'hidden' }}>
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: [
          'radial-gradient(ellipse 60% 40% at 10% 0%, rgba(101,79,111,0.30), transparent 60%)',
          'radial-gradient(ellipse 55% 40% at 90% 30%, rgba(255,109,0,0.16), transparent 60%)',
          'radial-gradient(ellipse 80% 40% at 50% 100%, rgba(26,35,126,0.45), transparent 60%)',
        ].join(','),
      }} />
      <MarketingNav />

      <main data-src={source} style={{
        position: 'relative', zIndex: 1, maxWidth: 1120, margin: '0 auto',
        padding: isMobile ? '100px 16px 64px' : '140px 32px 96px',
      }}>
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            <div style={{ padding: '0 4px' }}>{intro}</div>
            {demo}
            {offer}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 410px', gap: 64, alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
              <div>{intro}</div>
              {offer}
            </div>
            {demo}
          </div>
        )}

        {/* Nutzen */}
        <div style={{
          display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? 12 : 16, marginTop: isMobile ? 40 : 88,
        }}>
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} {...rise(0.3 + i * 0.06)} style={{
              padding: isMobile ? 20 : 24, borderRadius: 16,
              background: 'rgba(246,238,228,0.04)', border: '1px solid rgba(246,238,228,0.08)',
              display: isMobile ? 'flex' : 'block', gap: 16, alignItems: 'flex-start',
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12, display: 'grid', placeItems: 'center', flexShrink: 0,
                background: `${b.color}1F`, color: b.color, marginBottom: isMobile ? 0 : 16,
              }}>
                <b.icon size={20} />
              </div>
              <div>
                <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 17, fontWeight: 700, color: '#F6EEE4', margin: '0 0 6px' }}>
                  {b.title}
                </h3>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, lineHeight: 1.6, color: 'rgba(246,238,228,0.55)', margin: 0 }}>
                  {b.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Kontakt */}
        <div style={{
          marginTop: isMobile ? 36 : 56, paddingTop: 28, borderTop: '1px solid rgba(246,238,228,0.08)',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: 1.6, color: 'rgba(246,238,228,0.7)', margin: 0 }}>
            Lieber erst kurz sprechen? Die Einrichtung lässt sich in 15 Minuten gemeinsam klären.
          </p>
          <a
            href="mailto:support@candlear.de?subject=Erstgespr%C3%A4ch%20Candlear%20Booking"
            className="karte-btn karte-btn-ghost"
          >
            <Mail size={18} /> Gespräch anfragen
          </a>
        </div>
      </main>

      <MarketingFooter />

      {/* Simulierte E-Mail aus der Demo */}
      <AnimatePresence>
        {notice && (
          <motion.div
            key={notice.kind}
            role="status"
            initial={{ opacity: 0, y: reduceMotion ? 0 : -16, scale: reduceMotion ? 1 : 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: reduceMotion ? 0 : -12 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            className="karte-toast"
          >
            <div className="karte-toast-icon">
              {notice.kind === 'code' ? <Mail size={18} /> : <PartyPopper size={18} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {notice.kind === 'code' ? (
                <>
                  <div className="karte-toast-kicker">Neue E-Mail an {notice.email}</div>
                  <div className="karte-toast-title">
                    Dein Bestätigungscode: <span className="karte-toast-code">{notice.code}</span>
                  </div>
                  <div className="karte-toast-sub">Gib ihn im Widget ein. In der Demo kommt der Code hier statt per E-Mail.</div>
                </>
              ) : (
                <>
                  <div className="karte-toast-kicker">Termin gebucht</div>
                  <div className="karte-toast-title">{notice.when}</div>
                  <div className="karte-toast-sub">So einfach buchen auch deine Kundinnen und Kunden.</div>
                </>
              )}
            </div>
            <button type="button" className="karte-toast-close" onClick={closeNotice} aria-label="Hinweis schließen">
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .karte-device {
          padding: 8px; border-radius: 30px;
          background: linear-gradient(160deg, #241A45 0%, #15112C 50%, #3a1d12 100%);
          box-shadow:
            0 0 0 1px rgba(246,238,228,.10) inset,
            0 2px 6px rgba(0,0,0,.3),
            0 30px 80px -20px rgba(255,109,0,.35),
            0 60px 120px -40px rgba(101,79,111,.5);
        }
        .karte-btn {
          display: inline-flex; align-items: center; gap: 10px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 15px; font-weight: 700;
          padding: 14px 24px; border-radius: 12px; border: 0; cursor: pointer; text-decoration: none;
          transition: transform 180ms cubic-bezier(.34,1.56,.64,1), opacity 150ms ease;
        }
        .karte-btn:hover { transform: translateY(-2px); }
        .karte-btn:active { transform: translateY(0) scale(.98); }
        .karte-btn:focus-visible { outline: 2px solid #FF9A00; outline-offset: 3px; }
        .karte-btn-primary {
          color: white; background: linear-gradient(135deg, #FF8A00, #FF6D00 55%, #E65100);
          box-shadow: 0 1px 0 rgba(255,255,255,.25) inset, 0 10px 30px -10px rgba(255,109,0,.7);
        }
        .karte-btn-ghost { color: #F6EEE4; background: rgba(246,238,228,.06); box-shadow: 0 0 0 1px rgba(246,238,228,.18) inset; }
        .karte-code {
          display: inline-flex; align-items: center; gap: 14px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 20px; font-weight: 800; letter-spacing: .22em;
          color: #FF9A00; background: rgba(14,11,30,.55); border: 1.5px dashed rgba(255,154,0,.6);
          padding: 11px 18px 11px 22px; border-radius: 12px; cursor: pointer;
          transition: transform 180ms cubic-bezier(.34,1.56,.64,1);
        }
        .karte-code:hover { transform: translateY(-2px); }
        .karte-code:active { transform: scale(.98); }
        .karte-code:focus-visible { outline: 2px solid #FF9A00; outline-offset: 3px; }
        .karte-toast {
          position: fixed; z-index: 1000; top: 80px; left: 50%; translate: -50% 0;
          width: min(440px, calc(100vw - 24px));
          display: flex; gap: 14px; align-items: flex-start;
          padding: 16px 14px 16px 16px; border-radius: 18px;
          background: rgba(21,17,44,.96); backdrop-filter: blur(12px);
          border: 1px solid rgba(255,109,0,.35);
          box-shadow: 0 2px 6px rgba(0,0,0,.3), 0 24px 60px -20px rgba(255,109,0,.45);
          font-family: 'Inter', sans-serif; color: #F6EEE4;
        }
        .karte-toast-icon {
          width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0; display: grid; place-items: center;
          color: white; background: linear-gradient(135deg, #FF8A00, #E65100);
        }
        .karte-toast-kicker { font-size: 12px; color: rgba(246,238,228,.55); margin-bottom: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .karte-toast-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 15.5px; line-height: 1.35; }
        .karte-toast-code { color: #FF9A00; letter-spacing: .14em; font-weight: 800; user-select: all; }
        .karte-toast-sub { font-size: 13px; line-height: 1.45; color: rgba(246,238,228,.6); margin-top: 4px; }
        .karte-toast-close {
          flex-shrink: 0; width: 28px; height: 28px; border-radius: 8px; border: 0; cursor: pointer;
          display: grid; place-items: center; color: rgba(246,238,228,.6); background: rgba(246,238,228,.06);
          transition: transform 150ms ease;
        }
        .karte-toast-close:hover { color: #F6EEE4; }
        .karte-toast-close:active { transform: scale(.94); }
        .karte-toast-close:focus-visible { outline: 2px solid #FF9A00; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { .karte-btn, .karte-code { transition: none; } }
      `}</style>
    </div>
  )
}
